import { createSlice } from '@reduxjs/toolkit'
import {
  addPlan,
  calculateCriticalRiskRatio,
  calculateShortTermCriticalRiskRatio,
  countRisksByCriticality,
  deletePlan,
  getPlanbyId,
  getPlans,
  getPlansByMissionId,
  getSecurityControlsByPlan,
  updatePlan,
} from './planActions'
import {
  calculateCriticalRiskRatioType,
  calculateShortTermCriticalRiskRatioType,
  calculateShortTermRiskRatioType,
  countRisksByCriticalityType,
  getSecurityControlsByPlanType,
  Plan,
  PlanResponse,
} from '../../types/planTypes'

interface PlanState {
  plans: Plan[]
  loading: boolean
  plan: Plan | null
  error: string | null
  addedPlan: Plan | null
  updatedPlan: Plan | null
  PlanResponse: PlanResponse | null
  calculateCriticalRisk: calculateCriticalRiskRatioType | null
  calculateShortTermCriticalRisk: calculateShortTermCriticalRiskRatioType | null
  calculateShortTermRisk: calculateShortTermRiskRatioType | null
  countRisksByCriticality: countRisksByCriticalityType | null
  getSecurityControlsByPlan: getSecurityControlsByPlanType | null
}

const initialState: PlanState = {
  plans: [],
  loading: false,
  error: null,
  addedPlan: null,
  plan: null,
  updatedPlan: null,
  PlanResponse: null,

  calculateCriticalRisk: null,
  calculateShortTermCriticalRisk: null,
  calculateShortTermRisk: null,
  countRisksByCriticality: null,
  getSecurityControlsByPlan: null,
}

export const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPlans.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlans.fulfilled, (state, action) => {
        state.loading = false
        state.PlanResponse = {
          plans: action.payload.plans,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
          status: action.payload.status,
          message: action.payload.message,
        }
      })
      .addCase(getPlans.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.loading = false
        const deletedPlanId = action.meta.arg

        if (state.PlanResponse) {
          state.PlanResponse.plans = (state.PlanResponse.plans ?? []).filter(
            (plan) => plan._id !== deletedPlanId
          )

          state.PlanResponse.total = state.PlanResponse.total ?? 0
          state.PlanResponse.totalPages = state.PlanResponse.totalPages ?? 0
          state.PlanResponse.currentPage = state.PlanResponse.currentPage ?? 0
        }
      })
      .addCase(addPlan.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addPlan.fulfilled, (state, action) => {
        state.loading = false
        state.addedPlan = action.payload
        state.plans.push(action.payload)
      })
      .addCase(addPlan.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(updatePlan.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.loading = false
        state.updatedPlan = action.payload
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getPlanbyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlanbyId.fulfilled, (state, action) => {
        state.loading = false
        state.plan = action.payload
      })
      .addCase(getPlanbyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getPlansByMissionId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getPlansByMissionId.fulfilled, (state, action) => {
        state.loading = false
        state.PlanResponse = {
          plans: action.payload.plans,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getPlansByMissionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(calculateCriticalRiskRatio.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(calculateCriticalRiskRatio.fulfilled, (state, action) => {
        state.loading = false
        state.calculateCriticalRisk = {
          ratio: action.payload.ratio ?? 0,
        }
      })
      .addCase(calculateCriticalRiskRatio.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(calculateShortTermCriticalRiskRatio.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        calculateShortTermCriticalRiskRatio.fulfilled,
        (state, action) => {
          state.loading = false
          state.calculateShortTermCriticalRisk = {
            ratio: action.payload.ratio ?? 0,
          }
        }
      )
      .addCase(
        calculateShortTermCriticalRiskRatio.rejected,
        (state, action) => {
          state.loading = false
          state.error = action.error.message || 'Something went wrong'
        }
      )

      .addCase(countRisksByCriticality.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(countRisksByCriticality.fulfilled, (state, action) => {
        state.loading = false
        state.countRisksByCriticality = {
          counts: {
            Critical: action.payload.counts.Critical ?? 0,
            High: action.payload.counts.High ?? 0,
            Medium: action.payload.counts.Medium ?? 0,
            Low: action.payload.counts.Low ?? 0,

            // Si le type inclut d'autres propriétés, vous devez les initialiser ici
          },
        }
      })
      .addCase(countRisksByCriticality.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(getSecurityControlsByPlan.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getSecurityControlsByPlan.fulfilled, (state, action) => {
        state.loading = false

        state.getSecurityControlsByPlan = {
          securityControlsByRisk: action.payload.securityControlsByRisk || {},
        }
      })
      .addCase(getSecurityControlsByPlan.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
  },
})

export default planSlice.reducer
